// src/pages/DocumentiPage.tsx
import React, { useEffect, useMemo, useState, useRef } from 'react';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, Chip, IconButton, TablePagination, Collapse, Button,
  Divider, CircularProgress, Alert, Snackbar, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, ToggleButtonGroup, ToggleButton,
  Tooltip, Stack, Grid, LinearProgress
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import UpdateIcon from '@mui/icons-material/Update';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import RestoreIcon from '@mui/icons-material/Restore';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import SaveIcon from '@mui/icons-material/Save';
import UndoIcon from '@mui/icons-material/Undo';
import ClearIcon from '@mui/icons-material/Clear';

import { DocumentItem, DocDiff } from '../types/storage';
import { documentService, ScrapingStatus, SuppressedItem } from '../services/documentService';
import { useComuneConfig } from '../contexts/ConfigContext';

/* ───────────────────────── HELPERS ───────────────────────── */
const renderValue = (v: any) => {
  if (v === null || v === undefined) return '—';
  if (Array.isArray(v)) {
    return (
      <ul style={{ margin: 0, paddingLeft: 16 }}>
        {v.map((el, i) => <li key={i}>{renderValue(el)}</li>)}
      </ul>
    );
  }
  if (typeof v === 'object') {
    return <Box component="pre" sx={{ whiteSpace: 'pre-wrap', m: 0 }}>{JSON.stringify(v, null, 2)}</Box>;
  }
  return v.toString();
};

const DiffTable: React.FC<{ diff: DocDiff }> = ({ diff }) => (
  <Box mb={2}>
    <Typography variant="subtitle2">🔍 Differenze rilevate</Typography>
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell><strong>Chiave</strong></TableCell>
          <TableCell><strong>Originale</strong></TableCell>
          <TableCell><strong>Nuovo</strong></TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {Object.entries(diff).map(([k, { oldVal, newVal }]) => (
          <TableRow key={k}>
            <TableCell sx={{ width: 160 }}>{k}</TableCell>
            <TableCell sx={{ whiteSpace: 'pre-wrap' }}>{renderValue(oldVal)}</TableCell>
            <TableCell sx={{ whiteSpace: 'pre-wrap', bgcolor: '#fff8e1' }}>
              {renderValue(newVal)}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </Box>
);

/* ╭─────────────────────────────────────╮
   │  KEY/VALUE EDITOR (griglia per righe) │
   ╰─────────────────────────────────────╯ */

type KVPair = { key: string; value: string; originalValue: string };

type ParsedDoc = {
  freeText: string;
  pairs: KVPair[];
};

function parseTextToPairs(text: string): ParsedDoc {
  const lines = text.replace(/\r\n/g, '\n').split('\n');
  const pairs: KVPair[] = [];
  let currentKey: string | null = null;
  let currentValueLines: string[] = [];
  let freeTextLines: string[] = [];
  let sawFirstKey = false;

  const flush = () => {
    if (currentKey !== null) {
      const value = currentValueLines.join('\n').trimEnd();
      pairs.push({ key: currentKey, value, originalValue: value });
    }
    currentKey = null;
    currentValueLines = [];
  };

  for (const rawLine of lines) {
    const line = rawLine;
    const keyMatch = line.match(/^([^:\n]+?):\s*(.*)$/);
    if (keyMatch) {
      const key = keyMatch[1].trim();
      const firstVal = keyMatch[2] ?? '';
      if (currentKey !== null) flush();
      currentKey = key;
      currentValueLines = [firstVal];
      sawFirstKey = true;
    } else {
      if (!sawFirstKey) {
        freeTextLines.push(line);
      } else {
        currentValueLines.push(line);
      }
    }
  }
  flush();

  const freeText = freeTextLines.join('\n').trimEnd();
  return { freeText, pairs };
}

function buildTextFromPairs(model: ParsedDoc): string {
  const out: string[] = [];
  if (model.freeText) out.push(model.freeText.trimEnd());
  for (const { key, value } of model.pairs) {
    const vLines = value.replace(/\r\n/g, '\n').split('\n');
    const first = vLines[0] ?? '';
    const rest = vLines.slice(1);
    out.push(`${key}: ${first}`);
    for (const r of rest) out.push(r);
  }
  return out.join('\n').replace(/\s+$/g, '');
}

const KeyValueEditor: React.FC<{
  rawText: string;
  onChange: (nextText: string) => void;
}> = ({ rawText, onChange }) => {
  const [model, setModel] = useState<ParsedDoc>(() => parseTextToPairs(rawText));

  useEffect(() => {
    setModel(parseTextToPairs(rawText));
  }, [rawText]);

  const emit = (next: ParsedDoc) => {
    setModel(next);
    onChange(buildTextFromPairs(next));
  };

  const updateValue = (idx: number, value: string) => {
    const next = { ...model, pairs: model.pairs.map((p, i) => i === idx ? { ...p, value } : p) };
    emit(next);
  };

  const resetValue = (idx: number) => {
    const next = {
      ...model,
      pairs: model.pairs.map((p, i) => i === idx ? { ...p, value: p.originalValue } : p),
    };
    emit(next);
  };

  const clearValue = (idx: number) => {
    const next = {
      ...model,
      pairs: model.pairs.map((p, i) => i === idx ? { ...p, value: '' } : p),
    };
    emit(next);
  };

  return (
    <Box>
      {model.freeText && (
        <Box mb={3}>
          <Typography variant="subtitle2" sx={{ mb: 0.5, opacity: 0.7 }}>
            Testo iniziale (senza parola-chiave)
          </Typography>
          <TextField
            value={model.freeText}
            onChange={(e) => {
              const next = { ...model, freeText: e.target.value };
              emit(next);
            }}
            multiline
            minRows={4}
            fullWidth
          />
        </Box>
      )}

      <Grid container spacing={2}>
        {model.pairs.map((p, idx) => (
          <React.Fragment key={`${p.key}-${idx}`}>
            <Grid item xs={12} md={4}>
              <TextField
                label="Parola-chiave"
                value={p.key}
                fullWidth
                InputProps={{ readOnly: true }}
                sx={{
                  '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: '#0d47a1' },
                  '& .MuiInputBase-input': { fontWeight: 700, color: '#0d47a1' },
                }}
              />
            </Grid>
            <Grid item xs={12} md={8}>
              <TextField
                label="Valore"
                value={p.value}
                onChange={(e) => updateValue(idx, e.target.value)}
                multiline
                minRows={4}
                fullWidth
              />
              <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                <Button size="small" startIcon={<UndoIcon />} onClick={() => resetValue(idx)}>
                  Ripristina
                </Button>
                <Button size="small" startIcon={<ClearIcon />} onClick={() => clearValue(idx)}>
                  Svuota
                </Button>
              </Stack>
            </Grid>
          </React.Fragment>
        ))}
      </Grid>
    </Box>
  );
};

/* ──────────────────────── MAIN PAGE ──────────────────────── */
type ViewMode = 'attivi' | 'modificati' | 'rimossi';

const POLL_MS = 2_000;

const PHASE_LABEL: Record<string, string> = {
  discovery: 'Ricerca servizi…',
  scraping: 'Indicizzazione',
  finalizing: 'Finalizzazione…',
  approving: 'Approvazione…',
};

const DocumentiPage: React.FC = () => {
  const comune = useComuneConfig();
  const autoApprove = comune.features.ragAutoApprove;

  const [view, setView] = useState<ViewMode>('attivi');

  const [documenti, setDocumenti] = useState<DocumentItem[]>([]);
  const [rimossi, setRimossi] = useState<SuppressedItem[]>([]);
  const [modificati, setModificati] = useState<DocumentItem[]>([]);

  const [filtroNome, setFiltroNome] = useState('');

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [dettagliAperti, setDettagliAperti] = useState<string | null>(null);
  const [testoLetto, setTestoLetto] = useState<Record<string, string>>({});
  const [diffData, setDiffData] = useState<Record<string, DocDiff>>({});
  const [loadingId, setLoadingId] = useState<string | null>(null);
  const [errMsg, setErrMsg] = useState<Record<string, string>>({});

  const [busy, setBusy] = useState(false);
  const [snack, setSnack] = useState<{open:boolean; msg:string; severity:'success'|'error'|'info'|'warning' }>({open:false, msg:'', severity:'success'});

  const [askScrape, setAskScrape] = useState(false);

  const [askRemove, setAskRemove] = useState<{open:boolean; doc?: DocumentItem}>({open:false});
  const [removeReason, setRemoveReason] = useState('');

  const [editDlg, setEditDlg] = useState<{
    open: boolean;
    doc?: DocumentItem;
    originalText: string;
    editedText: string;
    saving?: boolean;
  }>({ open: false, originalText: '', editedText: '' });

  // ───── Avanzamento reale dello scraping (polling su /api/scraping-status) ─────
  const [prog, setProg] = useState<ScrapingStatus>({
    running: false, done: 0, total: 0, phase: 'idle', ok: null,
  });
  const timerRef = useRef<number | null>(null);

  // cleanup
  useEffect(() => {
    return () => { if (timerRef.current) window.clearInterval(timerRef.current); };
  }, []);

  // helpers caricamento
  const loadActives = async () => {
    setBusy(true);
    try {
      const items = await documentService.listDocuments();
      setDocumenti(items);
    } catch (e:any) {
      setSnack({open:true, msg:'Errore caricamento documenti', severity:'error'});
      console.error(e);
    } finally {
      setBusy(false);
    }
  };

  const loadRemoved = async () => {
    setBusy(true);
    try {
      const items = await documentService.listRemovedDocuments();
      setRimossi(items);
    } catch (e:any) {
      setSnack({open:true, msg:'Errore caricamento rimossi', severity:'error'});
      console.error(e);
    } finally {
      setBusy(false);
    }
  };

  const loadModified = async () => {
    setBusy(true);
    try {
      const items = await documentService.listModifiedDocuments();
      setModificati(items);
    } catch (e:any) {
      setSnack({open:true, msg:'Errore caricamento modificati', severity:'error'});
      console.error(e);
    } finally {
      setBusy(false);
    }
  };

  // Ricarica la tab attiva *ora*, non quella di quando è partito il polling
  const reloadRef = useRef<() => void>(() => {});
  reloadRef.current = () => {
    if (view === 'attivi') loadActives();
    else if (view === 'modificati') loadModified();
    else loadRemoved();
  };

  useEffect(() => {
    if (view === 'attivi') loadActives();
    else if (view === 'modificati') loadModified();
    else loadRemoved();

    setPage(0);
    setDettagliAperti(null);
    setTestoLetto({});
    setDiffData({});
    setErrMsg({});
  }, [view]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleDettagli = (doc: DocumentItem) => {
    const id = doc.doc_id;
    const nowOpen = dettagliAperti === id ? null : id;
    setDettagliAperti(nowOpen);

    if (nowOpen && doc.status === 'pending' && !diffData[id]) {
      (async () => {
        try {
          const d = await documentService.getDocumentDiff(id);
          setDiffData(prev => ({ ...prev, [id]: d }));
        } catch (e) {
          console.error(e);
          setErrMsg(prev => ({ ...prev, [id]: 'Impossibile calcolare le differenze' }));
        }
      })();
    }
  };

  const handleToggleTesto = async (doc: DocumentItem) => {
    const id = doc.doc_id;
    if (testoLetto[id]) {
      setTestoLetto(prev => { const n = { ...prev }; delete n[id]; return n; });
      return;
    }
    try {
      setLoadingId(id);
      const txt = await documentService.getDocumentText(id);
      setTestoLetto(prev => ({ ...prev, [id]: txt }));
    } catch (e) {
      console.error(e);
      setErrMsg(prev => ({ ...prev, [id]: 'Errore durante la lettura del file' }));
    } finally {
      setLoadingId(null);
    }
  };

  const handleApprove = async (docId: string) => {
    try {
      await documentService.approveDocument(docId);
      await loadActives();
      setSnack({open:true, msg:'Modifiche approvate', severity:'success'});
    } catch (e) {
      console.error(e);
      setSnack({open:true, msg:'Errore approvazione', severity:'error'});
    }
  };

  const handleReject = async (docId: string) => {
    if (!window.confirm('Rifiutare e cancellare la versione modificata?')) return;
    try {
      await documentService.rejectDocument(docId);
      await loadActives();
      setSnack({open:true, msg:'Modifica rifiutata', severity:'success'});
    } catch (e) {
      console.error(e);
      setSnack({open:true, msg:'Errore rifiuto', severity:'error'});
    }
  };

  const askRemoveFor = (doc: DocumentItem) => {
    setRemoveReason('');
    setAskRemove({open:true, doc});
  };
  const confirmRemove = async () => {
    if (!askRemove.doc) return;
    try {
      await documentService.suppressDocument(askRemove.doc.doc_id, removeReason);
      setAskRemove({open:false});
      setSnack({open:true, msg:'Documento rimosso', severity:'success'});
      await loadActives();
    } catch (e) {
      console.error(e);
      setSnack({open:true, msg:'Errore rimozione', severity:'error'});
    }
  };

  const handleRestore = async (docId: string) => {
    try {
      await documentService.unsuppressDocument(docId);
      setSnack({open:true, msg:'Documento ripristinato', severity:'success'});
      await loadRemoved();
    } catch (e) {
      console.error(e);
      setSnack({open:true, msg:'Errore ripristino', severity:'error'});
    }
  };

  const handleDelete = async (docId: string) => {
    if (!window.confirm('Vuoi eliminare definitivamente il documento?')) return;
    try {
      await documentService.deleteDocument(docId);
      setSnack({open:true, msg:'Documento eliminato', severity:'success'});
      await loadModified();
    } catch (e) {
      console.error(e);
      setSnack({open:true, msg:'Errore eliminazione', severity:'error'});
    }
  };

  // ───── PROGRESS REALE: polling finché lo scraper gira ─────
  const startPolling = () => {
    if (timerRef.current) window.clearInterval(timerRef.current);
    timerRef.current = window.setInterval(async () => {
      // un poll fallito non interrompe l'aggiornamento in corso
      const status = await documentService.getScrapingStatus().catch(() => null);
      if (!status) return;
      setProg(status);

      if (!status.running) {
        if (timerRef.current) window.clearInterval(timerRef.current);
        timerRef.current = null;
        reloadRef.current();
        // Tre stati, non due. `ok === null` è il default a cui il backend
        // torna quando viene riavviato a metà giro: annunciarlo come completato
        // lascia un aggiornamento interrotto che nessuno rilancerà.
        if (status.ok === true) {
          setSnack({ open: true, msg: 'Aggiornamento completato', severity: 'success' });
        } else if (status.ok === false) {
          setSnack({ open: true, msg: 'Aggiornamento terminato con errori', severity: 'error' });
        } else {
          setSnack({
            open: true,
            msg: 'Esito dell’aggiornamento sconosciuto: potrebbe non essere stato completato',
            severity: 'warning',
          });
        }
      }
    }, POLL_MS);
  };

  // Se lo scraping è già in corso (es. pagina ricaricata), riaggancia la barra
  useEffect(() => {
    documentService.getScrapingStatus()
      .then(status => { if (status.running) { setProg(status); startPolling(); } })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Avvio scraping: chiude dialog, chiama backend e aggancia il polling
  const doScrape = async () => {
    setAskScrape(false);
    try {
      await documentService.triggerScraping();
      setProg({ running: true, done: 0, total: 0, phase: 'discovery', ok: null });
      startPolling();
      setSnack({open:true, msg:'Aggiornamento della conoscenza avviato', severity:'info'});
    } catch (e: any) {
      // 409: un giro è già in corso. Non è un errore dell'operatore — ci si
      // riaggancia all'avanzamento esistente invece di annunciare un guasto.
      if (e?.response?.status === 409) {
        const p = e.response.data?.progress;
        if (p) setProg(p);
        startPolling();
        setSnack({open:true, msg:'Aggiornamento già in corso', severity:'info'});
        return;
      }
      console.error(e);
      setSnack({open:true, msg:'Errore avvio aggiornamento', severity:'error'});
    }
  };

  const handleOpenEdit = async (doc: DocumentItem) => {
    try {
      let text = testoLetto[doc.doc_id];
      if (!text) {
        setLoadingId(doc.doc_id);
        text = await documentService.getDocumentText(doc.doc_id);
      }
      setEditDlg({
        open: true,
        doc,
        originalText: text,
        editedText: text,
      });
    } catch (e:any) {
      console.error(e);
      setSnack({open:true, msg:'Errore apertura editor', severity:'error'});
    } finally {
      setLoadingId(null);
    }
  };

  const handleEditSave = async () => {
    if (!editDlg.doc) return;
    try {
      setEditDlg(prev => ({...prev, saving:true}));
      await documentService.manualSave(editDlg.doc.doc_id, editDlg.editedText);
      setSnack({open:true, msg:'Modifica salvata (manuale)', severity:'success'});
      setEditDlg({ open:false, originalText:'', editedText:'' });
      await Promise.all([loadActives(), loadModified()]);
    } catch (e:any) {
      console.error(e);
      setSnack({open:true, msg:'Errore salvataggio', severity:'error'});
    } finally {
      setEditDlg(prev => ({...prev, saving:false}));
    }
  };

  const rows = useMemo(() => {
    let out: DocumentItem[];
    if (view === 'attivi') {
      const actives = documenti.filter(d => d.status !== 'removed' && d.status !== 'manual');
      out = autoApprove ? actives.filter(d => d.status !== 'pending') : actives;
    } else if (view === 'modificati') out = modificati;
    else out = rimossi;

    // Ricerca per nome file. Il filtro sta qui perché la lista arriva intera
    // dall'API e la paginazione è già client-side: filtrare a monte dello slice
    // fa sì che il conteggio della paginazione segua la ricerca.
    const q = filtroNome.trim().toLowerCase();
    return q ? out.filter(d => (d.filename ?? '').toLowerCase().includes(q)) : out;
  }, [view, documenti, modificati, rimossi, autoApprove, filtroNome]);

  return (
    <Box p={3}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2} gap={2}>
        <Typography variant="h4">📄 Documenti</Typography>

        <Stack direction="row" gap={1} alignItems="center">
          <TextField
            size="small"
            label="Cerca nome file"
            value={filtroNome}
            onChange={e => { setFiltroNome(e.target.value); setPage(0); }}
            InputProps={{
              endAdornment: filtroNome ? (
                <IconButton size="small" onClick={() => { setFiltroNome(''); setPage(0); }}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              ) : undefined,
            }}
            sx={{ minWidth: 220 }}
          />

          <ToggleButtonGroup
            value={view}
            exclusive
            onChange={(_, v: ViewMode) => v && setView(v)}
            size="small"
          >
            <ToggleButton value="attivi">Attivi</ToggleButton>
            <ToggleButton value="modificati">Modificati</ToggleButton>
            <ToggleButton value="rimossi">Rimossi</ToggleButton>
          </ToggleButtonGroup>

          <Tooltip title="Aggiorna conoscenza dell'assistente vocale">
            <span>
              <Button
                variant="outlined"
                startIcon={<UpdateIcon />}
                onClick={() => setAskScrape(true)}
                disabled={busy || prog.running}
              >
                Aggiorna conoscenza
              </Button>
            </span>
          </Tooltip>

          {/* Barra piccola accanto al bottone */}
          {prog.running && (
          <Stack direction="row" alignItems="center" gap={1} sx={{ minWidth: 240 }}>
            <Box sx={{ width: 160 }}>
              {/* Prima della scoperta dei servizi il totale non esiste ancora */}
              {prog.total > 0
                ? <LinearProgress variant="determinate" value={Math.round((prog.done / prog.total) * 100)} />
                : <LinearProgress />}
            </Box>
            <Typography variant="caption" sx={{ minWidth: 84, textAlign: 'right' }}>
              {prog.total > 0 && prog.phase === 'scraping'
                ? `${prog.done}/${prog.total} servizi`
                : PHASE_LABEL[prog.phase] ?? ''}
            </Typography>
          </Stack>
        )}
        </Stack>
      </Stack>

      <Paper elevation={2} sx={{ opacity: busy ? 0.7 : 1 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Nome file</strong></TableCell>
                <TableCell><strong>Stato</strong></TableCell>
                {view === 'rimossi' && <TableCell><strong>Motivo rimozione</strong></TableCell>}
                {view === 'rimossi' && <TableCell><strong>Rimosso il</strong></TableCell>}
                <TableCell><strong>Ultima modifica</strong></TableCell>
                <TableCell />
              </TableRow>
            </TableHead>

            <TableBody>
              {rows
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((doc: any) => {
                  const open = dettagliAperti === doc.doc_id;
                  const isPending = doc.status === 'pending';
                  const isRemoved = view === 'rimossi';
                  const isManual = doc.status === 'manual';

                  return (
                    <React.Fragment key={doc.doc_id}>
                      <TableRow hover>
                        <TableCell>{doc.filename}</TableCell>
                        <TableCell>
                          {isRemoved ? (
                            <Chip label="Rimosso" color="error" size="small" icon={<WarningAmberIcon />} />
                          ) : (
                            <Chip
                              label={isPending ? 'Da revisionare' : (isManual ? 'Modificato manualmente' : 'Indicizzato')}
                              color={isPending ? 'warning' : (isManual ? 'info' : 'success')}
                              size="small"
                            />
                          )}
                        </TableCell>

                        {isRemoved && (
                          <TableCell sx={{ maxWidth: 360 }}>
                            {doc.suppressed_reason || '—'}
                          </TableCell>
                        )}

                        {isRemoved && (
                          <TableCell>
                            {doc.suppressed_at
                              ? new Date(doc.suppressed_at).toLocaleString('it-IT')
                              : '—'}
                          </TableCell>
                        )}

                        <TableCell>
                          {doc.updated_at ? new Date(doc.updated_at).toLocaleString('it-IT') : '—'}
                        </TableCell>

                        <TableCell>
                          <Stack direction="row" gap={1} justifyContent="flex-end">
                            {!isRemoved ? (
                              <Tooltip title="Dettagli / differenze / anteprima / azioni">
                                <IconButton size="small" onClick={() => toggleDettagli(doc)}>
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            ) : (
                              <Tooltip title="Ripristina documento">
                                <span>
                                  <Button
                                    size="small"
                                    variant="contained"
                                    color="success"
                                    startIcon={<RestoreIcon />}
                                    onClick={() => handleRestore(doc.doc_id)}
                                  >
                                    Ripristina
                                  </Button>
                                </span>
                              </Tooltip>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>

                      {!isRemoved && (
                        <TableRow>
                          <TableCell colSpan={6} sx={{ p: 0 }}>
                            <Collapse in={open} timeout="auto" unmountOnExit>
                              <Box m={2} p={2} border={1} borderColor="#ddd" borderRadius={2} bgcolor="#fafafa">
                                <Typography variant="subtitle1" gutterBottom>Dettagli</Typography>
                                <Divider sx={{ mb: 2 }} />

                                <Typography variant="body2" mb={2}><strong>ID documento:</strong> {doc.doc_id}</Typography>

                                {isPending && diffData[doc.doc_id] && <DiffTable diff={diffData[doc.doc_id]} />}

                                {errMsg[doc.doc_id] && <Alert severity="error">{errMsg[doc.doc_id]}</Alert>}

                                <Box display="flex" gap={2} mt={2} flexWrap="wrap">
                                  {isPending && !autoApprove && (
                                    <>
                                      <Button
                                        variant="contained"
                                        color="success"
                                        onClick={() => handleApprove(doc.doc_id)}
                                        startIcon={<RestartAltIcon />}
                                      >
                                        Accetta modifiche
                                      </Button>
                                      <Button
                                        variant="outlined"
                                        color="error"
                                        onClick={() => handleReject(doc.doc_id)}
                                      >
                                        Rifiuta
                                      </Button>
                                    </>
                                  )}

                                  <Button variant="outlined" onClick={() => handleToggleTesto(doc)}>
                                    {loadingId === doc.doc_id
                                      ? 'Caricamento…'
                                      : testoLetto[doc.doc_id] ? 'Chiudi anteprima' : 'Mostra anteprima'}
                                  </Button>

                                  {/* 🔒 Nascondi Modifica servizio se pending */}
                                  {!isPending && (
                                    <Button
                                      variant="contained"
                                      onClick={() => handleOpenEdit(doc)}
                                    >
                                      Modifica servizio
                                    </Button>
                                  )}

                                  {view === 'attivi' && (
                                    <Tooltip title="Rimuovi con motivo (opzionale)">
                                      <span>
                                        <Button
                                          size="small"
                                          variant="outlined"
                                          color="error"
                                          startIcon={<DeleteForeverIcon />}
                                          onClick={() => askRemoveFor(doc)}
                                        >
                                          Rimuovi…
                                        </Button>
                                      </span>
                                    </Tooltip>
                                  )}

                                  {view === 'modificati' && (
                                    <Tooltip title="Elimina definitivamente (documento e contenuti indicizzati)">
                                      <span>
                                        <Button
                                          size="small"
                                          variant="outlined"
                                          color="error"
                                          startIcon={<DeleteForeverIcon />}
                                          onClick={() => handleDelete(doc.doc_id)}
                                        >
                                          Elimina
                                        </Button>
                                      </span>
                                    </Tooltip>
                                  )}
                                </Box>

                                {testoLetto[doc.doc_id] && (
                                  <Box mt={3}>
                                    <Typography variant="subtitle2" gutterBottom>Testo del file</Typography>
                                    <Box
                                      sx={{
                                        whiteSpace: 'pre-wrap',
                                        background: '#fff',
                                        p: 2,
                                        border: '1px solid #ccc',
                                        borderRadius: 1,
                                        maxHeight: 400,
                                        overflowY: 'auto'
                                      }}
                                    >
                                      {testoLetto[doc.doc_id]}
                                    </Box>
                                  </Box>
                                )}
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}

              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={view === 'rimossi' ? 6 : 5} align="center">
                    {busy ? <CircularProgress size={20} /> : 'Nessun documento'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={rows.length}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={e => { setRowsPerPage(+e.target.value); setPage(0); }}
          rowsPerPageOptions={[5, 10, 25]}
        />
      </Paper>

      {/* DIALOG: Aggiorna conoscenza */}
      <Dialog open={askScrape} onClose={() => setAskScrape(false)}>
        <DialogTitle>Aggiorna conoscenza dell'assistente vocale</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Vuoi avviare l’aggiornamento della conoscenza del Voicebot?
            L’operazione può richiedere alcuni minuti e i contenuti potrebbero cambiare.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAskScrape(false)}>Annulla</Button>
          <Button variant="contained" onClick={doScrape} startIcon={<UpdateIcon />}>
            Avvia aggiornamento
          </Button>
        </DialogActions>
      </Dialog>

      {/* DIALOG: motivo rimozione (solo per Attivi) */}
      <Dialog open={askRemove.open} onClose={() => setAskRemove({open:false})} fullWidth maxWidth="sm">
        <DialogTitle>Rimuovi documento</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Confermi di voler rimuovere il documento? Verrà escluso dalla ricerca e spostato in “Rimossi”.
            Puoi inserire un motivo (opzionale).
          </Typography>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={2}
            label="Motivo della rimozione (opzionale)"
            value={removeReason}
            onChange={(e) => setRemoveReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAskRemove({open:false})}>Annulla</Button>
          <Button variant="contained" color="error" onClick={confirmRemove} startIcon={<DeleteForeverIcon />}>
            Rimuovi
          </Button>
        </DialogActions>
      </Dialog>

      {/* DIALOG: Modifica manuale */}
      <Dialog
        open={editDlg.open}
        onClose={() => setEditDlg({ open:false, originalText:'', editedText:'' })}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Modifica servizio (blocca aggiornamenti automatici)</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Le parole-chiave a sinistra sono bloccate. A destra puoi modificare o svuotare i valori.
          </Typography>

          <KeyValueEditor
            rawText={editDlg.originalText}
            onChange={(t) => setEditDlg(prev => ({ ...prev, editedText: t }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDlg({ open:false, originalText:'', editedText:'' })}>
            Annulla
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleEditSave}
            disabled={editDlg.saving}
          >
            {editDlg.saving ? 'Salvataggio…' : 'Salva modifica'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={2800}
        onClose={() => setSnack(s => ({...s, open:false}))}
        message={snack.msg}
      />
    </Box>
  );
};

export default DocumentiPage;
